import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';

// Logo oficial AgencySMM animado con GSAP (vectorial inline, no Remotion mp4).
// Inlinear el SVG nos deja animar cada parte por separado (tilde, badge,
// wordmark/marca) y recolorear según el fondo. Reemplaza el clip Remotion
// viejo del sidebar y los textos placeholder de login/dashboard.
//
//  variant 'v1'  → lockup horizontal "AgencySMM" (login + dashboard).
//  variant 'ico' → marca cuadrada (sidebar, arriba a la izquierda).
//  tone 'ink'    → fill oscuro #1A1A1A (sobre fondo claro).
//  tone 'gradient' → gradiente oficial lime→cyan (sobre fondo oscuro).

type Variant = 'v1' | 'ico';
// 'ink' = fill oscuro fijo · 'gradient' = lime→cyan oficial · 'auto' = currentColor
// (hereda el color del texto del theme → legible en claro y oscuro).
type Tone = 'ink' | 'gradient' | 'auto';

interface AnimatedLogoProps {
  variant: Variant;
  tone?: Tone;
  /** Altura en px; el ancho se deriva del viewBox. */
  height?: number;
  /** Loop sutil de flotado tras la entrada. */
  idle?: boolean;
  className?: string;
  title?: string;
}

const INK = '#1A1A1A';

export function AnimatedLogo({
  variant,
  tone = 'ink',
  height = 40,
  idle = false,
  className,
  title = 'AgencySMM',
}: AnimatedLogoProps) {
  const rootRef = useRef<SVGSVGElement>(null);
  const gradId = `asmm-grad-${variant}`;
  const fill = tone === 'gradient' ? `url(#${gradId})` : tone === 'auto' ? 'currentColor' : INK;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // Respetar reduced-motion: dejamos todo visible (los gsap.from parten del
    // estado final del DOM, así que simplemente no animamos).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      if (variant === 'v1') {
        tl.from('[data-word]', { opacity: 0, x: -22, duration: 0.6 })
          .from('[data-dot]', {
            opacity: 0, scale: 0, duration: 0.5,
            ease: 'back.out(3.2)', svgOrigin: '9084.44 474.21',
          }, '-=0.25')
          .from('[data-badge]', { opacity: 0, y: 18, scale: 0.9, duration: 0.5, svgOrigin: '6675 2680' }, '-=0.3');
      } else {
        tl.from('[data-mark]', { opacity: 0, y: 16, scale: 0.84, duration: 0.6, svgOrigin: '1860 1860' })
          .from('[data-dot]', {
            opacity: 0, scale: 0, duration: 0.5,
            ease: 'back.out(3.2)', svgOrigin: '3346.85 467.22',
          }, '-=0.2')
          .from('[data-badge]', { opacity: 0, y: 20, scale: 0.9, duration: 0.5, svgOrigin: '1310 2920' }, '-=0.3');
      }

      if (idle) {
        tl.to(root, { y: -4, duration: 2.6, ease: 'sine.inOut', yoyo: true, repeat: -1 }, '>-0.2');
      }
    }, rootRef);

    return () => ctx.revert();
  }, [variant, idle]);

  const common = {
    ref: rootRef,
    className,
    role: 'img' as const,
    'aria-label': title,
    style: { height, width: 'auto' as const, display: 'block', overflow: 'visible' as const },
  };

  if (variant === 'ico') {
    return (
      <svg {...common} viewBox="0 0 3820.66 3405.48" xmlns="http://www.w3.org/2000/svg">
        <Defs id={gradId} />
        <g data-mark fill={fill}>
          <polygon
            fillRule="nonzero"
            points="3119.96,806.2 2852.85,1537.57 2848.31,1537.57 2622.54,806.2 2220.52,806.2 2645.02,2003.17 2136.74,3152.01 2536.48,3152.01 3519.72,806.2 "
          />
          <path
            fillRule="nonzero"
            d="M1833.04 1823.75l72.86 243.02 465.62 0 -606.97 -1680.78 -338.44 0 -735.37 1680.78 461.07 0 97.51 -243.02 583.72 0zm-259.95 -915.34l4.53 0 2.28 0 151.73 574.65 -352.04 0 193.5 -574.65z"
          />
        </g>
        <circle data-dot fill={fill} cx="3346.85" cy="467.22" r="212.68" />
        <path
          data-badge
          fill={fill}
          d="M530.61 2614.75l1559.7 0c41.95,0 61.33,34.31 43.07,76.28l-167.39 384.7c-18.26,41.96 -67.53,76.28 -109.47,76.28l-1559.69 0c-41.95,0 -61.36,-34.3 -43.09,-76.28l167.4 -384.7c18.27,-41.98 67.5,-76.28 109.47,-76.28zm320.39 185.56c-0.2,-22.66 -0.89,-49.62 -23.47,-78.75 -24.8,-30.77 -66.9,-45.34 -106.27,-45.34 -66.9,0 -141.2,42.62 -143.99,117.61 -2.7,74.98 65.86,103.04 93.44,114.37 60.67,25.88 77.1,32.36 76.18,58.8 -0.69,18.34 -15.96,38.31 -40.25,38.31 -12.95,0 -41.3,-8.1 -39.57,-54.49l-101.42 0 -0.4 12.93c-1.92,51.26 23.97,126.78 139.42,126.78 105.65,0 141.4,-64.2 143.52,-122.45 1.65,-46.39 -15.89,-80.91 -89.11,-114.35 -70.62,-32.39 -81.25,-37.24 -80.37,-60.98 0.7,-18.88 18.35,-31.29 36.25,-31.29 31.29,0 33.63,25.35 35.26,38.85l100.78 0zm41.63 282.66l93.84 0 7.63 -299.4 1.08 -0.52 74.38 299.92 67.42 0 96.19 -299.92 0.99 0.52 -14.09 299.4 93.86 0 14.45 -399.18 -131.55 0 -87.82 262.43 -1.07 0 -69.8 -262.43 -130.99 0 -14.52 399.18zm493.59 0l93.86 0 7.61 -299.4 1.09 -0.52 74.37 299.92 67.44 0 96.18 -299.92 0.98 0.52 -14.1 299.4 93.88 0 14.43 -399.18 -131.53 0 -87.83 262.43 -1.08 0 -69.78 -262.43 -131.01 0 -14.51 399.18z"
        />
      </svg>
    );
  }

  return (
    <svg {...common} viewBox="0 0 9553.84 3405.46" xmlns="http://www.w3.org/2000/svg">
      <Defs id={gradId} />
      <path
        data-word
        fill={fill}
        fillRule="nonzero"
        d="M1393.57 1830.74l72.86 243.02 465.62 0 -606.97 -1680.78 -338.44 0 -735.37 1680.78 461.07 0 97.51 -243.02 583.72 0zm-259.95 -915.34l4.53 0 2.28 0 151.73 574.65 -352.04 0 193.5 -574.65zm2306.85 -102.21l-377.04 0 -4.03 118.09 -4.53 0c-64.28,-109.02 -201.2,-158.97 -342.01,-158.97 -327.07,0 -644.93,247.58 -660.25,679.11 -14.81,404.3 268.61,663.23 611.59,663.23 197.6,0 310.81,-106.75 351.85,-179.45l4.56 0 -1.45 38.63c-12.12,338.42 -271.51,342.95 -319.2,342.95 -24.98,0 -165.82,0 -215.82,-124.9l-435.71 0c18.37,179.41 174.53,442.9 612.9,442.9 311.16,0 545.7,-138.56 648.85,-297.56 65.13,-106.74 83.21,-227.11 89.97,-415.64l40.32 -1108.39zm-1010.95 624.6c6.71,-183.98 154.76,-313.44 316.01,-313.44 168.1,0 302.18,122.65 295.15,324.81 -6.68,181.7 -138.88,313.44 -320.58,313.44 -156.72,0 -297.61,-122.65 -290.58,-324.81zm2076.58 240.77c-54.57,65.85 -117.96,118.11 -233.8,118.11 -147.62,0 -269.44,-88.58 -286.48,-238.48l944.88 0c5.48,-29.54 10.73,-49.98 12.4,-99.96 14.08,-383.83 -256.44,-688.2 -640.29,-688.2 -383.86,0 -672.46,324.81 -685.2,676.85 -13.52,374.77 282.18,667.77 643.32,667.77 129.46,0 255.84,-38.61 362.77,-113.56 109.59,-72.7 202.03,-181.72 268.51,-322.53l-386.11 0zm-506.59 -374.77c18.72,-74.95 98.5,-215.76 293.83,-215.76 195.35,0 265.47,140.81 278.53,215.76l-572.36 0zm1130.54 769.97l377.04 0 24.79 -685.92c2.9,-77.23 9.63,-263.49 225.42,-263.49 197.61,0 191.46,172.63 188.38,261.21l-25.22 688.2 377.05 0 28.51 -788.13c8.96,-247.58 -64.57,-347.54 -130.51,-406.58 -65.96,-59.05 -198.3,-109.02 -318.69,-109.02 -224.86,0 -308.67,115.83 -349.53,177.16l-4.53 0 5.04 -134 -352.07 0 -45.68 1260.57zm2343.12 -479.25c-43.78,77.23 -121.87,168.09 -278.59,168.09 -179.42,0 -295.09,-136.28 -288.38,-320.25 6.68,-181.72 132.33,-329.35 311.58,-320.25 156.72,0 228.16,90.83 266.01,177.16l381.58 0c-52.39,-308.91 -314.59,-529.23 -648.47,-529.23 -377.02,0 -676.13,299.81 -689.6,670.04 -13.52,374.76 268.43,674.58 650,674.58 340.7,0 601.18,-233.96 679.73,-520.14l-383.86 0zm1384.38 -781.32l-267.11 731.37 -4.54 0 -225.77 -731.37 -402.02 0 424.5 1196.97 -508.28 1148.84 399.74 0 983.24 -2345.81 -399.76 0z"
      />
      <circle data-dot fill={fill} cx="9084.44" cy="474.21" r="212.68" />
      <path
        data-badge
        fill={fill}
        d="M5433.29 2303.08l2484.76 0c66.83,0 97.71,54.67 68.63,121.53l-266.68 612.87c-29.08,66.85 -107.58,121.52 -174.4,121.52l-2484.74 0c-66.83,0 -97.76,-54.64 -68.65,-121.52l266.68 -612.87c29.11,-66.88 107.55,-121.53 174.4,-121.53zm510.42 295.63c-0.32,-36.11 -1.42,-79.06 -37.39,-125.47 -39.52,-49.01 -106.58,-72.21 -169.3,-72.21 -106.58,0 -224.94,67.89 -229.39,187.35 -4.3,119.46 104.91,164.15 148.85,182.21 96.66,41.23 122.84,51.56 121.37,93.67 -1.1,29.22 -25.43,61.03 -64.12,61.03 -20.63,0 -65.8,-12.9 -63.04,-86.81l-161.58 0 -0.64 20.61c-3.06,81.65 38.2,201.97 222.12,201.97 168.31,0 225.26,-102.29 228.64,-195.08 2.63,-73.91 -25.32,-128.9 -141.96,-182.18 -112.51,-51.59 -129.44,-59.31 -128.04,-97.14 1.12,-30.07 29.24,-49.84 57.75,-49.84 49.85,0 53.58,40.37 56.18,61.89l160.55 0zm66.32 450.31l149.5 0 12.15 -476.98 1.72 -0.83 118.49 477.81 107.42 0 153.23 -477.81 1.58 0.83 -22.45 476.98 149.53 0 23.01 -635.95 -209.56 0 -139.9 418.09 -1.72 0 -111.19 -418.09 -208.69 0 -23.12 635.95zm786.33 0l149.53 0 12.13 -476.98 1.74 -0.83 118.47 477.81 107.44 0 153.23 -477.81 1.56 0.83 -22.46 476.98 149.56 0 22.99 -635.95 -209.54 0 -139.93 418.09 -1.71 0 -111.17 -418.09 -208.71 0 -23.13 635.95z"
      />
    </svg>
  );
}

// Gradiente oficial (lime→cyan) tomado de Logo_ASMM_color.svg. Solo se usa
// cuando tone='gradient' (fondo oscuro); en 'ink' el fill es plano.
function Defs({ id }: { id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="0.15">
        <stop offset="0" stopColor="#99FF00" />
        <stop offset="1" stopColor="#37C1EF" />
      </linearGradient>
    </defs>
  );
}
